import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, EmptyState } from "@/components/system";
import { Field } from "@/routes/FinanceAccounts";

type TxnRow = {
  id: string;
  ts: string;
  amount: number;
  currency: string;
  description: string | null;
  merchant: string | null;
  category_id: string | null;
  account_id: string | null;
  source: string;
};

export function FinanceTransactions() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate();

  const txns = useQuery({
    queryKey: ["finance_transactions", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 60);
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_transaction")
        .select("id, ts, amount, currency, description, merchant, category_id, account_id, source")
        .eq("user_id", userId!)
        .gte("ts", since.toISOString())
        .order("ts", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as TxnRow[];
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-s)",
          }}
        >
          Transactions
        </h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate("/finance/transactions/new")}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          + Log
        </button>
      </header>

      {txns.isLoading ? (
        <SkeletonLoader variant="card" height={300} />
      ) : (txns.data ?? []).length === 0 ? (
        <EmptyState
          headline="No transactions in the last 60 days."
          sub="Log a transaction or upload a receipt to start."
        />
      ) : (
        <div
          style={{
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            background: "var(--bg-2)",
            overflow: "hidden",
          }}
        >
          {(txns.data ?? []).map((t) => {
            const isExpense = t.amount < 0;
            return (
              <div
                key={t.id}
                style={{
                  padding: "12px 16px",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 12,
                  borderBottom: "1px solid var(--hair)",
                }}
              >
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)", minWidth: 64 }}>
                  {new Date(t.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: "var(--type-body-s)", fontWeight: 500 }}>
                    {t.merchant ?? t.description ?? "(untitled)"}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--fg-3)" }}>{t.source}</span>
                </div>
                <span
                  className="mono"
                  style={{
                    fontSize: "var(--type-body)",
                    fontWeight: 500,
                    color: isExpense ? "var(--severity-bad, #c53e2e)" : "var(--severity-ok, #4a8755)",
                  }}
                >
                  {formatMoney(t.amount, t.currency, { sign: "auto" })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FinanceTransactionNew() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [direction, setDirection] = useState<"expense" | "income">("expense");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CHF");
  const [merchant, setMerchant] = useState("");
  const [description, setDescription] = useState("");
  const [accountId, setAccountId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [ts, setTs] = useState(new Date().toISOString().slice(0, 16));

  const accounts = useQuery({
    queryKey: ["finance_accounts_picker", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_account")
        .select("id, display_name, currency")
        .eq("user_id", userId!)
        .eq("closed", false)
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as { id: string; display_name: string; currency: string }[];
    },
  });

  const categories = useQuery({
    queryKey: ["finance_categories_picker", userId, direction],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .select("id, display_name, kind")
        .eq("user_id", userId!)
        .eq("archived", false)
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as { id: string; display_name: string; kind: string }[];
    },
  });

  const mutate = useMutation({
    mutationFn: async () => {
      const num = Number(amount);
      const signed = direction === "expense" ? -Math.abs(num) : Math.abs(num);
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_transaction")
        .insert({
          user_id: userId,
          account_id: accountId,
          category_id: categoryId,
          ts: new Date(ts).toISOString(),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          amount: signed,
          currency,
          description: description.trim() || null,
          merchant: merchant.trim() || null,
          source: "manual",
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance_transactions", userId] });
      queryClient.invalidateQueries({ queryKey: ["finance_cashflow_month", userId] });
      navigate("/finance/transactions");
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)", maxWidth: 520, margin: "0 auto" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)" }}>
        <Link to="/finance/transactions" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Back
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 24 }}>
          Log transaction
        </h1>
      </header>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => setDirection("expense")}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: 10,
            border: "1px solid var(--hair-2)",
            background: direction === "expense" ? "var(--accent)" : "transparent",
            color: direction === "expense" ? "var(--accent-on)" : "var(--fg)",
            cursor: "pointer",
          }}
        >
          Expense
        </button>
        <button
          onClick={() => setDirection("income")}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: 10,
            border: "1px solid var(--hair-2)",
            background: direction === "income" ? "var(--accent)" : "transparent",
            color: direction === "income" ? "var(--accent-on)" : "var(--fg)",
            cursor: "pointer",
          }}
        >
          Income
        </button>
      </div>

      <Field label="Amount">
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            style={{ ...inputStyle, flex: 1 }}
          />
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...inputStyle, width: 110 }}>
            {["CHF", "EUR", "USD", "GBP", "JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </Field>
      <Field label="Merchant / source">
        <input value={merchant} onChange={(e) => setMerchant(e.target.value)} style={inputStyle} placeholder="e.g. Coop, Salary" />
      </Field>
      <Field label="Description">
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Account">
        <select value={accountId ?? ""} onChange={(e) => setAccountId(e.target.value || null)} style={inputStyle}>
          <option value="">— None —</option>
          {(accounts.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name} · {a.currency}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Category">
        <select value={categoryId ?? ""} onChange={(e) => setCategoryId(e.target.value || null)} style={inputStyle}>
          <option value="">— None —</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.display_name}</option>
          ))}
        </select>
      </Field>
      <Field label="When">
        <input type="datetime-local" value={ts} onChange={(e) => setTs(e.target.value)} style={inputStyle} />
      </Field>

      <button
        onClick={() => mutate.mutate()}
        disabled={!amount || mutate.isPending}
        style={{
          background: "var(--accent)",
          color: "var(--accent-on)",
          padding: "12px",
          border: "none",
          borderRadius: 10,
          fontSize: "var(--type-body)",
          fontWeight: 500,
          cursor: "pointer",
          opacity: amount ? 1 : 0.4,
        }}
      >
        {mutate.isPending ? "Saving…" : direction === "expense" ? "Log expense" : "Log income"}
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  background: "var(--bg)",
  color: "var(--fg)",
  fontSize: "var(--type-body-s)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};
